import type { Road, ZoneName } from '../content/types';
import type { Fields } from './fields';
import { hash, hashStr, Rng } from './rng';
import { RoadNet, SEG, SPACING, type Axis } from './roads';
import { PIECE } from './overlay';

/** Direction the lot's front (street side) faces. 0 = south (+y), 1 = west, 2 = north, 3 = east. */
export type Facing = 0 | 1 | 2 | 3;

export interface Lot {
  key: string;
  x: number; y: number; w: number; h: number; // world tiles, axis-aligned
  facing: Facing;
  zone: ZoneName;
  /** frontage length (tiles) and depth (tiles) */
  front: number; depth: number;
  /** in a player-zoned cell: the building is a construction site until a crew has built it (overlay.ts) */
  planned?: boolean;
  /** (§57) in a city's inner core: the building fills the lot — the widest that fits, flush, no set-back */
  tight?: true;
  /** (owner, Sep 22) bare ground the generator left (or a bought plot with no design yet): a plot for sale (world/custom.ts, game/growth.ts plotAt) */
  vacant?: true;
}

interface Row { axis: Axis; pos: number; side: 1 | -1; seg: number; road: Road; roadW: number; zone: ZoneName; depth: number; /** a player-built piece: the stretch it covers (a generated line spans its whole segment) */ span?: [number, number]; planned?: boolean }

/**
 * Lots hang off road *rows*: one row per (line, segment, side). A row is cut into spans
 * at crossings; each span gets a run of lots. Where two rows meet at a corner, a hash
 * of the intersection decides which row owns the corner square, so lots never overlap.
 * Depth is clamped to half the gap to the next parallel road so opposite rows never
 * overlap either. Everything is keyed by integers → identical from any chunk.
 */
export class LotPlanner {
  constructor(readonly fields: Fields, readonly net: RoadNet) {}

  /** All lots whose rectangle intersects [x0,x1)×[y0,y1). Deterministic. */
  lotsIn(x0: number, y0: number, x1: number, y1: number): Lot[] {
    const out: Lot[] = [];
    const margin = 72; // max lot depth + road
    for (const axis of ['v', 'h'] as Axis[]) {
      const [p0, p1] = axis === 'v' ? [x0 - margin, x1 + margin] : [y0 - margin, y1 + margin];
      const [u0, u1] = axis === 'v' ? [y0, y1] : [x0, x1];
      const seg0 = Math.floor(u0 / SEG), seg1 = Math.floor((u1 - 1) / SEG);
      const step = SPACING[3];
      for (let pos = Math.ceil(p0 / step) * step; pos <= p1; pos += step) {
        for (let seg = seg0; seg <= seg1; seg++) {
          for (const side of [-1, 1] as const) {
            const row = this.row(axis, pos, side, seg);
            if (row && !row.span) this.lotsForRow(row, out, x0, y0, x1, y1);
          }
        }
      }
      // player-built pieces: one row per piece and side (a piece is 16 tiles, never a whole segment)
      const ov = this.fields.overlay;
      if (ov.roads.size) for (const r of ov.roads.values()) {
        if (r.a !== axis || r.b < PIECE || r.p < p0 || r.p > p1 || r.f + PIECE <= u0 - margin || r.f >= u1 + margin) continue;
        for (const side of [-1, 1] as const) {
          const row = this.row(axis, r.p, side, Math.floor(r.f / SEG), r.f + PIECE / 2);
          if (row?.span) this.lotsForRow(row, out, x0, y0, x1, y1);
        }
      }
    }
    return out;
  }

  /** Row descriptor, or null if the line does not exist / no room. Pure function of its key (`u`: where along the line to look — a player-built piece only covers part of a segment). */
  row(axis: Axis, pos: number, side: 1 | -1, seg: number, u = (seg + 0.5) * SEG): Row | null {
    const l = this.net.lineAt(axis, pos, u);
    if (!l) return null;
    const roadW = this.net.laneWidth(axis, l.level, l.k, seg, l.road);
    const [zx, zy] = axis === 'v' ? [pos + side * 6, u] : [u, pos + side * 6];
    const planned = l.span ? this.fields.overlay.zoneAt(zx, zy) : undefined; // only player-built pieces get lots in player-zoned cells (a generated line's row spans its whole segment)
    const zone = planned ?? this.fields.urbanZone(zx, zy);
    if (zone === 'water') return null;
    if (planned && l.road.class === 'rail') return null; // nothing hangs off a railway
    if (!planned && zone === 'rural' && this.fields.density(zx, zy) < 0.02) return null; // wilderness: highways only, no lots (unless a planner zoned it)
    const z = this.fields.zone(zone);
    const gap = this.net.gapToParallel(axis, pos, u, side);
    const depth = Math.min(z.lotDepth, Math.floor((gap - roadW) / 2));
    if (depth < 2) return null;
    return { axis, pos, side, seg, road: l.road, roadW, zone, depth, span: l.span, planned: !!planned };
  }

  /** Near edge (first tile) of a row measured from the line. */
  static nearEdge(pos: number, roadW: number, side: 1 | -1, depth: number): number {
    return side === 1 ? pos + Math.floor(roadW / 2) + 1 : pos - Math.floor((roadW - 1) / 2) - depth;
  }

  private lotsForRow(row: Row, out: Lot[], x0: number, y0: number, x1: number, y1: number): void {
    const { axis, pos, side, seg } = row;
    const z = this.fields.zone(row.zone);
    const [segStart, segEnd] = row.span ?? [seg * SEG, seg * SEG + SEG];
    const perp: Axis = axis === 'v' ? 'h' : 'v';
    const cross = this.net.crossings(axis, pos + side, segStart, segEnd + 1);
    const bounds = [segStart, ...cross.map((c) => c.at), segEnd].filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => a - b);
    const near = LotPlanner.nearEdge(pos, row.roadW, side, row.depth);

    for (let i = 0; i + 1 < bounds.length; i++) {
      const a = bounds[i], b = bounds[i + 1];
      // trim each end: past the crossing road, and past the perpendicular row's lots unless we own the corner
      const from = this.trim(row, perp, a, 1, cross.some((c) => c.at === a));
      const to = this.trim(row, perp, b, -1, cross.some((c) => c.at === b));
      if (to - from < z.lotWidthMin) continue;
      const rowKey = row.span ? `plot:${axis}:${pos}:${row.span[0]}:${i}:${side}` : `lot:${axis}:${pos}:${seg}:${i}:${side}`;
      const rng = new Rng(hashStr(rowKey, this.fields.seed));
      let cur = from, n = 0;
      while (cur < to) {
        let w = rng.int(z.lotWidthMin, z.lotWidthMax);
        if (to - cur - w < z.lotWidthMin) w = to - cur; // absorb remainder
        if (w > z.lotWidthMax * 2) w = z.lotWidthMax;
        const facing: Facing = axis === 'v' ? (side === 1 ? 1 : 3) : side === 1 ? 2 : 0;
        const lot: Lot = axis === 'v'
          ? { key: `${rowKey}:${n}`, x: near, y: cur, w: row.depth, h: w, facing, zone: row.zone, front: w, depth: row.depth }
          : { key: `${rowKey}:${n}`, x: cur, y: near, w, h: row.depth, facing, zone: row.zone, front: w, depth: row.depth };
        if (row.planned) lot.planned = true;
        if (lot.x < x1 && lot.x + lot.w > x0 && lot.y < y1 && lot.y + lot.h > y0) out.push(lot);
        cur += w; n++;
      }
    }
  }

  /**
   * Where does this row's usable frontage start (dir=1) / end (dir=-1) at bound `at`?
   * If a perpendicular road crosses there, skip its width; if the perpendicular row on
   * our side owns the corner, also skip its depth.
   */
  private trim(row: Row, perp: Axis, at: number, dir: 1 | -1, isCrossing: boolean): number {
    if (!isCrossing) return at;
    const u = row.pos + row.side; // coordinate along the perpendicular line, just on our side
    const pl = this.net.lineAt(perp, at, u);
    if (!pl) return at;
    const pw = this.net.laneWidth(perp, pl.level, pl.k, Math.floor(u / SEG), pl.road);
    const edge = dir === 1 ? at + Math.floor(pw / 2) + 1 : at - Math.floor((pw - 1) / 2);
    // the competing perpendicular row: on the perp line at `at`, on the side our span lies (dir), in the segment containing u
    const perpSide: 1 | -1 = dir;
    const perpRow = this.row(perp, at, perpSide, Math.floor(u / SEG), u);
    if (!perpRow) return edge;
    // corner ownership: quadrant is (side along v-line, side along h-line)
    const [X, Y] = row.axis === 'h' ? [at, row.pos] : [row.pos, at];
    const [sx, sy] = row.axis === 'h' ? [dir, row.side] : [row.side, dir];
    const ownerIsH = (hash(this.fields.seed, X, Y, sx, sy, 5) & 1) === 0;
    const weOwn = (row.axis === 'h') === ownerIsH;
    return weOwn ? edge : edge + dir * perpRow.depth;
  }
}
